import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	MessageInstanceEventClient,
	type FrontendMessageInstanceEventWithoutRecipient,
} from '@atlassian/message-instance-event-frontend-client/message-instance-event-client';
import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';
import { useMessageInstances } from '@atlassian/post-office-headless-api/hooks/useMessageInstances';

import { skillsChangeboardShownStore } from './SkillsChangeboardShownStore';
import { type DismissSource, SkillsOnboardingSpotlight } from './SkillsOnboardingSpotlight';

const SKILLS_SPOTLIGHT_TEMPLATE_ID = 'rovo-chat-skills-onboarding-spotlight';
const SKILLS_ONBOARDING_FEATURE_ID = 'skills-onboarding';

type SkillsSpotlightMessage = {
	messageInstanceId: string;
	messageTemplateId: typeof SKILLS_SPOTLIGHT_TEMPLATE_ID;
	triggerId?: string;
	messageCreationType?: string;
	context: {
		featureId?: string;
	};
};

const toInAppMessageCreationType = (
	messageCreationType: string | undefined,
): 'implicit' | 'explicit' => (messageCreationType === 'explicit' ? 'explicit' : 'implicit');

type SkillsSpotlightControllerProps = {
	/** Whether skills are available in this chat input at all (mirrors `enableSkills`). */
	enableSkills: boolean;
	onViewSkills?: () => void;
	children: React.ReactNode;
};

export const SkillsSpotlightController = ({
	enableSkills,
	onViewSkills,
	children,
}: SkillsSpotlightControllerProps): React.JSX.Element => {
	// Read once per mount, not subscribed: the changeboard marks itself shown as it renders, and a
	// reactive read would unmount it mid-view. Suppression applies to the next mount instead.
	const [hasChangeboardBeenShown] = React.useState(skillsChangeboardShownStore.hasBeenShown);

	if (!enableSkills || hasChangeboardBeenShown) {
		return <>{children}</>;
	}

	// Feature-gate the rollout as the last term, after the cheap eligibility checks above. The gate
	// name must be a static string literal (@atlaskit/platform/static-feature-flags), so it is inlined
	// here rather than stored in a constant.
	if (!fg('post-office_rovo-chat-skills-changeboard')) {
		return <>{children}</>;
	}

	return (
		<SkillsSpotlightControllerInner onViewSkills={onViewSkills}>
			{children}
		</SkillsSpotlightControllerInner>
	);
};

type SkillsSpotlightControllerInnerProps = Pick<
	SkillsSpotlightControllerProps,
	'onViewSkills' | 'children'
>;

const SkillsSpotlightControllerInner = ({
	onViewSkills,
	children,
}: SkillsSpotlightControllerInnerProps): React.JSX.Element => {
	const postOfficeContext = usePostOfficeContext();
	const messageInstanceEventClientRef = React.useRef(new MessageInstanceEventClient());
	// Optimistically hide on dismiss/CTA so the spotlight closes immediately regardless of the Post
	// Office round-trip (dismissMessage also removes it from the placement result).
	const [isDismissed, setIsDismissed] = React.useState(false);
	const {
		messages: skillsSpotlightMessages,
		dismissMessage: dismissSkillsSpotlightMessage,
		isLoading: isSkillsSpotlightLoading,
	} = useMessageInstances<SkillsSpotlightMessage>({
		placementId: 'headless',
		messageTemplateId: SKILLS_SPOTLIGHT_TEMPLATE_ID,
	});

	const skillsOnboardingSpotlight = !isSkillsSpotlightLoading
		? skillsSpotlightMessages.find(
				(message: SkillsSpotlightMessage) =>
					message.context?.featureId === SKILLS_ONBOARDING_FEATURE_ID,
			)
		: undefined;

	const sentViewedEventIdsRef = React.useRef<Record<string, true>>({});
	const sentDeliverySuccessfulEventIdsRef = React.useRef<Record<string, true>>({});

	const emitVisibilityLifecycleEvents = React.useCallback(
		(message: SkillsSpotlightMessage | undefined) => {
			if (!message) {
				return;
			}
			const basePayload = {
				messageInstanceId: message.messageInstanceId,
				messageTemplateId: message.messageTemplateId,
				messageTriggerId: message.triggerId ?? '',
				messageCreationType: toInAppMessageCreationType(message.messageCreationType),
				deliveryChannel: 'in-app' as const,
				placementId: 'headless',
				channelType: 'pull' as const,
				context: {
					...postOfficeContext,
					source: 'headless',
					recommendation: {
						recommendationSessionId: '',
						recommendationEntityId: '',
						itemFeatureSetId: '',
					},
				},
				timestamp: Date.now(),
			};

			if (!sentViewedEventIdsRef.current[message.messageInstanceId]) {
				const sendViewedResult = messageInstanceEventClientRef.current.sendMessageInstanceEvent({
					...basePayload,
					messageEventType: 'viewed',
				});
				sentViewedEventIdsRef.current[message.messageInstanceId] = true;
				if (sendViewedResult) {
					void sendViewedResult.catch(() => {
						// Swallow lifecycle failures to avoid blocking product-owned spotlight rendering.
					});
				}
			}

			if (!sentDeliverySuccessfulEventIdsRef.current[message.messageInstanceId]) {
				const sendDeliveryResult = messageInstanceEventClientRef.current.sendMessageInstanceEvent({
					...basePayload,
					messageEventType: 'deliverySuccessful',
				});
				sentDeliverySuccessfulEventIdsRef.current[message.messageInstanceId] = true;
				if (sendDeliveryResult) {
					void sendDeliveryResult.catch(() => {
						// Swallow lifecycle failures to avoid blocking product-owned spotlight rendering.
					});
				}
			}
		},
		[postOfficeContext],
	);

	React.useEffect(() => {
		void emitVisibilityLifecycleEvents(skillsOnboardingSpotlight);
	}, [emitVisibilityLifecycleEvents, skillsOnboardingSpotlight]);

	// Keyed on the instance id rather than on `emitVisibilityLifecycleEvents`, which depends on
	// `postOfficeContext` and so would re-fire this on every render.
	const seenMessageInstanceId = skillsOnboardingSpotlight?.messageInstanceId;

	React.useEffect(() => {
		if (!seenMessageInstanceId) {
			return;
		}
		// Suppress for the rest of this page load, whether or not the user interacts. Post Office's
		// viewed-check-filter takes over from the next page load, keyed off the `viewed` event above.
		skillsChangeboardShownStore.markShown();
	}, [seenMessageInstanceId]);

	const emitClickAndDismiss = (source: DismissSource | 'view-skills-button') => {
		// ADS keeps its outside-click listener bound while the card unmounts, so a second click can
		// land after the first dismissal. Post Office must not get two `clicked` events for one card.
		if (!skillsOnboardingSpotlight || isDismissed) {
			return;
		}

		const clickPayload: FrontendMessageInstanceEventWithoutRecipient = {
			messageInstanceId: skillsOnboardingSpotlight.messageInstanceId,
			messageTemplateId: skillsOnboardingSpotlight.messageTemplateId,
			messageTriggerId: skillsOnboardingSpotlight.triggerId ?? '',
			messageCreationType: toInAppMessageCreationType(
				skillsOnboardingSpotlight.messageCreationType,
			),
			deliveryChannel: 'in-app',
			placementId: 'headless',
			channelType: 'pull',
			context: {
				...postOfficeContext,
				source: 'headless',
				recommendation: {
					recommendationSessionId: '',
					recommendationEntityId: '',
					itemFeatureSetId: '',
				},
			},
			timestamp: Date.now(),
			messageEventType: 'clicked' as const,
			clickedElement:
				source === 'view-skills-button'
					? {
							clickIdentifier: 'view-skills',
							clickedElement: 'button' as const,
							buttonType: 'primary' as const,
						}
					: {
							// `clickedElement` has no value for "clicked away", so the source lives here instead.
							clickIdentifier: source === 'cross-icon' ? 'dismiss' : 'lightDismiss',
							clickedElement: 'cross-icon' as const,
						},
			// 'dismiss' for every path, matching `MentionAgentsSpotlightController`. 'acknowledge' would
			// read better for the CTA, but Post Office's click/dismiss check filters do not recognise it
			// and would keep delivering the message. Revisit once they do.
			action: {
				actionType: 'dismiss' as const,
			},
			additionalMetadataFromFrontend: {},
		};

		const sendResult = messageInstanceEventClientRef.current.sendMessageInstanceEvent(clickPayload);
		if (sendResult) {
			void sendResult.catch(() => {
				// Allow optimistic dismiss to proceed even if event emission fails.
			});
		}
		dismissSkillsSpotlightMessage(skillsOnboardingSpotlight.messageInstanceId);
		setIsDismissed(true);
	};

	const handleViewSkills = () => {
		// Run the existing, already-implemented behavior first: insert `/` and open the skill picker.
		onViewSkills?.();
		emitClickAndDismiss('view-skills-button');
	};

	// Clicking away counts as a dismissal, not just the X, so the changeboard is not re-delivered to
	// someone who closed it the usual way.
	const handleDismiss = (source: DismissSource) => {
		emitClickAndDismiss(source);
	};

	return (
		<SkillsOnboardingSpotlight
			isVisible={!isDismissed && !!skillsOnboardingSpotlight}
			onViewSkills={handleViewSkills}
			onDismiss={handleDismiss}
		>
			{children}
		</SkillsOnboardingSpotlight>
	);
};
